import { ticketLog } from '@/utils/ticketLogger';

export const TICKET_CHECKOUT_CONTEXT_KEY = 'ticketCheckoutContext';
export const GUEST_CHECKOUT_EMAIL_KEY = 'unregisterEmail';
export const GUEST_CHECKOUT_PHONE_KEY = 'unregisterPhone';

export function saveGuestCheckoutContact(email: string, phone?: string): void {
  if (email) {
    localStorage.setItem(GUEST_CHECKOUT_EMAIL_KEY, email);
  }
  if (phone !== undefined) {
    localStorage.setItem(GUEST_CHECKOUT_PHONE_KEY, phone || '');
  }
}

export function loadGuestCheckoutContact(): { email: string; phone: string } {
  return {
    email: localStorage.getItem(GUEST_CHECKOUT_EMAIL_KEY) || '',
    phone: localStorage.getItem(GUEST_CHECKOUT_PHONE_KEY) || '',
  };
}

export interface CartLineSummary {
  lineType: 'Ticket' | 'Glow' | 'Sweepstakes' | 'Donation' | 'Sticker';
  stickerId: string;
  stickerTitle?: string;
  variantId?: string;
  variantTitle?: string;
  quantity?: number;
  unitCost?: number;
  donationAmount?: number;
  isFree?: boolean;
  donationCauseId?: string;
  donationCauseIds?: string[];
  sweepstakesEntries?: number;
  sweepstakesEntryCost?: number;
  campaignType?: string;
  selectedOption?: string;
}

export interface TicketCheckoutContext {
  stickerId: string;
  stickerIds: string[];
  orderId: string;
  expectedCount: number;
  existingTicketIds: string[];
  cartLineSummary?: CartLineSummary[];
  email?: string;
  phone?: string;
  embedOrganizationId?: string;
  embedSource?: string;
}

export interface TicketWithVariant {
  tierName: string;
  variantId: string;
}

export function buildVariantTitleMap(
  tiers: Array<{ variantId: string; title: string }>,
): Record<string, string> {
  const map: Record<string, string> = {};
  tiers.forEach((tier) => {
    if (tier.variantId && tier.title) map[tier.variantId] = tier.title;
  });
  return map;
}

export function buildVariantTitleMapFromSticker(
  variants: Array<{ id: string; title: string }> | undefined,
): Record<string, string> {
  const map: Record<string, string> = {};
  (variants || []).forEach((variant) => {
    if (variant.id && variant.title) map[variant.id] = variant.title;
  });
  return map;
}

export function resolveTicketTierName(
  ticket: TicketWithVariant,
  variantTitleMap: Record<string, string>,
): string {
  return variantTitleMap[ticket.variantId] || ticket.tierName;
}

export function saveTicketCheckoutContext(context: TicketCheckoutContext): void {
  ticketLog('Saving checkout context', context);
  sessionStorage.setItem(TICKET_CHECKOUT_CONTEXT_KEY, JSON.stringify(context));
}

export function loadTicketCheckoutContext(): TicketCheckoutContext | null {
  try {
    const raw = sessionStorage.getItem(TICKET_CHECKOUT_CONTEXT_KEY);
    if (!raw) {
      ticketLog('No checkout context in sessionStorage');
      return null;
    }
    const parsed = JSON.parse(raw) as TicketCheckoutContext;
    ticketLog('Loaded checkout context', parsed);
    return parsed;
  } catch (err) {
    ticketLog('Failed to parse checkout context', err);
    return null;
  }
}

export function clearTicketCheckoutContext(): void {
  ticketLog('Clearing checkout context');
  sessionStorage.removeItem(TICKET_CHECKOUT_CONTEXT_KEY);
}

export function countExpectedTicketsForSticker(
  cartLines: Array<{ lineType: string; stickerId: string; quantity?: number }>,
  stickerId: string,
): number {
  return cartLines
    .filter((line) => line.lineType === 'Ticket' && line.stickerId === stickerId)
    .reduce((sum, line) => sum + (line.quantity ?? 1), 0);
}

export function countAllExpectedTickets(
  cartLines: Array<{ lineType: string; quantity?: number }>,
): number {
  return cartLines
    .filter((line) => line.lineType === 'Ticket')
    .reduce((sum, line) => sum + (line.quantity ?? 1), 0);
}

export function getTicketStickerIds(
  cartLines: Array<{ lineType: string; stickerId: string }>,
): string[] {
  return [...new Set(
    cartLines
      .filter((line) => line.lineType === 'Ticket' && line.stickerId)
      .map((line) => line.stickerId),
  )];
}

export function getNewCheckoutTickets<T extends { id: string }>(
  allTickets: T[],
  context: TicketCheckoutContext | null,
): T[] {
  if (!context) return allTickets;

  const newTickets = allTickets.filter(
    (ticket) => !context.existingTicketIds.includes(ticket.id),
  );

  ticketLog('Filtered new checkout tickets', {
    totalFetched: allTickets.length,
    existingCount: context.existingTicketIds.length,
    newCount: newTickets.length,
    expectedCount: context.expectedCount,
    newTicketIds: newTickets.map((ticket) => ticket.id),
  });

  if (context.expectedCount > 0) {
    return newTickets.slice(0, context.expectedCount);
  }

  return newTickets;
}

export function hasAllCheckoutTickets<T extends { id: string }>(
  allTickets: T[],
  context: TicketCheckoutContext | null,
  fallbackExpectedCount: number,
): boolean {
  const expectedCount = context?.expectedCount || fallbackExpectedCount;
  if (!expectedCount) return allTickets.length > 0;

  const newTickets = context
    ? allTickets.filter((ticket) => !context.existingTicketIds.includes(ticket.id))
    : allTickets;

  const ready = newTickets.length >= expectedCount;
  ticketLog('Checking checkout ticket readiness', {
    expectedCount,
    newTicketsCount: newTickets.length,
    totalTicketsCount: allTickets.length,
    ready,
    hasContext: !!context,
  });

  return ready;
}

export function formatCheckoutErrorMessage(
  message: string,
  cartLines: Array<{ stickerId: string; stickerTitle?: string }>,
): string {
  if (!message) return message;

  const titleByStickerId = cartLines.reduce<Record<string, string>>((acc, line) => {
    if (line.stickerId && line.stickerTitle && !acc[line.stickerId]) {
      acc[line.stickerId] = line.stickerTitle;
    }
    return acc;
  }, {});

  let formatted = message;
  for (const [stickerId, title] of Object.entries(titleByStickerId)) {
    formatted = formatted.split(stickerId).join(title);
  }

  formatted = formatted.replace(
    /Sticker\s+([^.\n]+?)\s+not found\.?/gi,
    (_, name) => `${name.trim()} not found.`,
  );

  return formatted;
}

export function getNonTicketSummaries(
  summaries: CartLineSummary[] = [],
): CartLineSummary[] {
  return summaries.filter((line) => line.lineType !== 'Ticket');
}

export function formatOrderLineTitle(line: CartLineSummary): string {
  if (line.lineType === 'Ticket') {
    return line.variantTitle || line.stickerTitle || 'Ticket';
  }
  if (line.lineType === 'Donation') return 'Donation';
  if (line.lineType === 'Glow') return line.stickerTitle ? `${line.stickerTitle} — Glow` : 'Glow';
  if (line.lineType === 'Sweepstakes') {
    return line.stickerTitle ? `${line.stickerTitle} — Sweepstakes` : 'Sweepstakes';
  }
  if (line.lineType === 'Sticker') {
    if (line.donationCauseId || line.donationCauseIds?.length) {
      return line.stickerTitle || 'Donation';
    }
    if (line.sweepstakesEntryCost != null || line.sweepstakesEntries) {
      return line.stickerTitle || 'Sweepstakes';
    }
    return line.stickerTitle || 'Sticker';
  }
  return line.stickerTitle || 'Item';
}

function formatCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

export function formatOrderLineDetail(line: CartLineSummary): string {
  const qty = line.quantity ?? 1;

  if (line.lineType === 'Donation') {
    const amount = line.donationAmount ?? 0;
    const causeCount = line.donationCauseIds?.length ?? 1;
    if (causeCount > 1) {
      return `${causeCount} causes × ${formatCurrency(amount)}`;
    }
    return formatCurrency(amount);
  }

  if (line.lineType === 'Sticker') {
    if (line.donationCauseId || line.donationCauseIds?.length) {
      const amount = (line.unitCost ?? line.donationAmount ?? 0) * qty;
      const causeCount = line.donationCauseIds?.length ?? 1;
      if (causeCount > 1) {
        return `${formatCurrency(amount)} donation × ${causeCount} causes`;
      }
      return `${formatCurrency(amount)} donation confirmed`;
    }
    if (line.sweepstakesEntryCost != null || line.sweepstakesEntries) {
      const entries = line.sweepstakesEntries ?? qty;
      return `${entries} ${entries === 1 ? 'entry' : 'entries'} confirmed`;
    }
    if (line.isFree || (line.unitCost ?? 0) === 0) {
      return 'Ready to claim in Purchase History';
    }
    if (qty > 1) return `${qty} stickers confirmed`;
    return 'Purchase confirmed';
  }

  if (line.lineType === 'Sweepstakes') {
    const entries = line.sweepstakesEntries ?? 1;
    return `${entries} ${entries === 1 ? 'entry' : 'entries'} confirmed`;
  }

  if (line.lineType === 'Glow') {
    if (line.isFree || (line.unitCost ?? 0) === 0) return 'Included — confirmed';
    return `${formatCurrency(line.unitCost ?? 0)} confirmed`;
  }

  return 'Confirmed';
}

// Backwards-compatible alias
export const countExpectedTickets = countExpectedTicketsForSticker;
